/**
 * /accounting/reports/cash-flow — direct-method statement.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useSeedGeneralLedger } from "@/data/accounting/useSeedGeneralLedger";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import {
  computeCashFlow, listAvailablePeriods, formatPeriodLabel, type PeriodKey, type CashFlowRow,
} from "@/data/accounting/reports/financialReports";

const fmt = (n: number) =>
  `${n < 0 ? "-" : ""}$${Math.abs(Math.round(n)).toLocaleString()}`;

export default function CashFlowPage() {
  useSeedGeneralLedger();
  const { accounts, journalEntries } = useGeneralLedger();
  const periods = useMemo<PeriodKey[]>(
    () => ["all", ...listAvailablePeriods(journalEntries)],
    [journalEntries],
  );
  const [period, setPeriod] = useState<PeriodKey>(periods[1] ?? "all");
  const cf = useMemo(
    () => computeCashFlow(accounts, journalEntries, period),
    [accounts, journalEntries, period],
  );

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6">
        <Link to="/accounting/reports" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="h-3 w-3" /> Back to Reports
        </Link>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Cash Flow</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {formatPeriodLabel(period)} · direct method
            </p>
          </div>
          <select
            className="text-sm border border-border rounded-md px-2 py-1.5 bg-card"
            value={period}
            onChange={e => setPeriod(e.target.value)}
          >
            {periods.map(p => (
              <option key={p} value={p}>{formatPeriodLabel(p)}</option>
            ))}
          </select>
        </div>
      </div>

      <CockpitSection>
        <div className="grid grid-cols-3 gap-3">
          <Kpi label="Opening cash" value={fmt(cf.openingCash)} />
          <Kpi label="Net change" value={fmt(cf.netCashChange)} tone={cf.netCashChange < 0 ? "neg" : "pos"} />
          <Kpi label="Closing cash" value={fmt(cf.closingCash)} />
        </div>
      </CockpitSection>

      <CockpitSection>
        <div className="rounded-md border border-border bg-card divide-y divide-border">
          <Group label="Operating activities" rows={cf.operatingRows} />
          {cf.investingRows.length > 0 && <Group label="Investing activities" rows={cf.investingRows} />}
          {cf.financingRows.length > 0 && <Group label="Financing activities" rows={cf.financingRows} />}
          <div className="px-3 py-2 flex justify-between text-sm font-semibold bg-muted/30">
            <span>Net change in cash</span>
            <span className={`tabular-nums ${cf.netCashChange < 0 ? "text-destructive" : ""}`}>{fmt(cf.netCashChange)}</span>
          </div>
        </div>
      </CockpitSection>
    </CockpitPage>
  );
}

function Group({ label, rows }: { label: string; rows: CashFlowRow[] }) {
  const total = rows.reduce((s, r) => s + r.amount, 0);
  return (
    <div className="py-2">
      <div className="px-3 text-xs uppercase tracking-wide text-muted-foreground mb-1">{label}</div>
      {rows.length === 0 && (
        <div className="px-3 py-1 text-sm text-muted-foreground">No activity.</div>
      )}
      {rows.map(r => (
        <div key={r.label} className="px-3 py-1 flex justify-between text-sm">
          <span className="text-muted-foreground">{r.label}</span>
          <span className={`tabular-nums ${r.amount < 0 ? "text-destructive" : ""}`}>{fmt(r.amount)}</span>
        </div>
      ))}
      <div className="px-3 pt-1 mt-1 border-t border-border flex justify-between text-sm font-medium">
        <span>Total {label.toLowerCase()}</span>
        <span className={`tabular-nums ${total < 0 ? "text-destructive" : ""}`}>{fmt(total)}</span>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: "pos" | "neg" }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${tone === "neg" ? "text-destructive" : ""}`}>{value}</div>
    </div>
  );
}
